import { useApolloClient } from "@apollo/client/react"
import { IconButton } from "@mui/material"
import { useFormikContext } from "formik"
import { X } from "lucide-mui"
import { DateTime } from "luxon"
import React, { useEffect, useMemo, useState } from "react"

import { PlanViewer, type VenuePlanData } from "@/components/plan"
import HorizontalSchedule, { type BusyBlock } from "@/components/schedule/horizontal_schedule"
import { GetPublicBusyBlocksDocument } from "@/graphql/graphql"
import i18n from "@/i18n"
import type { ReservationFormValues } from "@/pages/public/venue"
import { formatTableName } from "@/shared/tables"

import { STYLES } from "./_styles"
import { OnboardingTooltip, type OnboardingTooltipConfig } from "./reservation_details_step/onboarding_tooltips"

interface Table {
  id: string
  name: string
  minCapacity?: number | null
  maxCapacity?: number | null
  tableGroup: {
    id: string
    name: string
  }
}

interface ReservationDetailsWithPlanProps {
  tables: Table[]
  venuePlans: VenuePlanData[]
  timeOptions: [string, DateTime][]
  venueSlug: string
  closedUntil?: DateTime | null
  onConflictChange?: (hasConflicts: boolean) => void
  showSchedule?: boolean
  excludeReservationId?: string | null
  scheduleOnboardingTooltip?: OnboardingTooltipConfig
}

const ReservationDetailsWithPlan: React.FC<ReservationDetailsWithPlanProps> = ({
  tables,
  venuePlans,
  timeOptions,
  venueSlug,
  closedUntil,
  onConflictChange,
  showSchedule = true,
  excludeReservationId,
  scheduleOnboardingTooltip,
}) => {
  const { values, setFieldValue } = useFormikContext<ReservationFormValues>()
  const [busyBlocks, setBusyBlocks] = useState<BusyBlock[]>([])
  const apolloClient = useApolloClient()

  const mapTables = tables.map((t) => ({ id: t.id, name: t.name }))
  const onTableSelect = (tableId: string) => setFieldValue("tableId", tableId)

  const disabledTables = useMemo(() => {
    if (!values.peopleAmount) return {}
    const result: Record<string, string> = {}
    tables.forEach((table) => {
      if (table.minCapacity && values.peopleAmount < table.minCapacity) {
        result[table.id] = i18n.t("app.settings.venues.venue_layout.table_capacity.min_required", {
          count: table.minCapacity,
        })
      } else if (table.maxCapacity && values.peopleAmount > table.maxCapacity) {
        result[table.id] = i18n.t("app.settings.venues.venue_layout.table_capacity.max_exceeded", {
          count: table.maxCapacity,
        })
      }
    })
    return result
  }, [tables, values.peopleAmount])

  useEffect(() => {
    if (!values.date || !values.tableId) {
      setBusyBlocks([])
      return
    }

    const dateStr = values.date.toISODate()
    if (!dateStr) return

    let cancelled = false

    apolloClient
      .query({
        query: GetPublicBusyBlocksDocument,
        variables: {
          slug: venueSlug,
          tableId: values.tableId,
          date: dateStr,
          ...(excludeReservationId ? { excludeReservationId } : {}),
        },
        fetchPolicy: "network-only",
      })
      .then((result) => {
        if (cancelled) return undefined
        const blocks =
          result.data?.public?.venue?.busyBlocks?.map((b) => ({
            startTime: b.startTime,
            endTime: b.endTime,
          })) || []
        setBusyBlocks(blocks)
        return undefined
      })
      .catch((error) => {
        if (cancelled || error?.name === "AbortError") return
        throw error
      })

    return () => {
      cancelled = true
    }
  }, [apolloClient, values.date, values.tableId, venueSlug, excludeReservationId])

  const [_hasConflicts, setHasConflicts] = useState(false)

  const selectedTable = useMemo(() => {
    if (!values.tableId) return null
    return tables.find((t) => t.id === values.tableId) || null
  }, [values.tableId, tables])

  const selectedTableName = useMemo(() => {
    if (!selectedTable) return null
    return formatTableName(selectedTable)
  }, [selectedTable])

  const selectedTimeRange = useMemo(() => {
    if (!values.startTime || !values.endTime) return null
    const start = DateTime.fromISO(values.startTime)
    const end = DateTime.fromISO(values.endTime)
    return `${start.toFormat("HH:mm")} - ${end.toFormat("HH:mm")}`
  }, [values.startTime, values.endTime])

  const scheduleHeader = (
    <div className="flex items-center justify-between" data-testid="public-reservation-modal-details-bottom-content">
      <div className="flex flex-wrap items-center gap-3" data-testid="public-reservation-modal-details-chips">
        {selectedTimeRange ? (
          <div className={STYLES.selectionChip} data-testid="public-reservation-modal-details-chip-time">
            <span>{selectedTimeRange}</span>
            <IconButton
              size="small"
              aria-label={i18n.t("app.public.venue.modal.clear_time_selection")}
              onClick={() => {
                void setFieldValue("startTime", "")
                void setFieldValue("endTime", "")
              }}
              sx={{ padding: 0, marginLeft: "2px" }}
              data-testid="public-reservation-modal-details-clear-time-btn"
            >
              <X className="w-2.5 h-2.5 text-[#9ca3af]" />
            </IconButton>
          </div>
        ) : null}
        {selectedTableName ? (
          <div
            className={`${STYLES.selectionChip} font-semibold`}
            data-testid="public-reservation-modal-details-chip-table"
          >
            <span>{selectedTableName}</span>
            <IconButton
              size="small"
              aria-label={i18n.t("app.public.venue.modal.clear_table_selection")}
              onClick={() => setFieldValue("tableId", "")}
              sx={{ padding: 0, marginLeft: "2px" }}
              data-testid="public-reservation-modal-details-clear-table-btn"
            >
              <X className="w-2.5 h-2.5 text-[#9ca3af]" />
            </IconButton>
          </div>
        ) : null}
      </div>
      <div className={STYLES.legend} data-testid="public-reservation-modal-details-map-legend">
        <div className={STYLES.legendItem}>
          <div className={STYLES.legendDot} style={{ backgroundColor: "#d9d9d9" }} />
          <span className={STYLES.legendText}>{i18n.t("app.schedule.legend.available")}</span>
        </div>
        <div className={STYLES.legendItem}>
          <div className={STYLES.legendDot} style={{ backgroundColor: "#a0b8d0" }} />
          <span className={STYLES.legendText}>{i18n.t("app.schedule.legend.occupied")}</span>
        </div>
        <div className={STYLES.legendItem}>
          <div className={STYLES.legendDot} style={{ backgroundColor: "#2596fa" }} />
          <span className={STYLES.legendText}>{i18n.t("app.schedule.legend.selected")}</span>
        </div>
      </div>
    </div>
  )

  const scheduleElement = (
    <HorizontalSchedule
      timeOptions={timeOptions}
      busyBlocks={busyBlocks}
      closedUntil={closedUntil}
      showFloatingTimeLabels={true}
      headerContent={scheduleHeader}
      onConflictChange={(conflicts) => {
        setHasConflicts(conflicts)
        onConflictChange?.(conflicts)
      }}
      onTimeChange={(start, end) => {
        void setFieldValue("startTime", start.toISO())
        void setFieldValue("endTime", end.toISO())
      }}
      currentReservation={
        values.startTime && values.endTime
          ? {
              startTime: values.startTime,
              endTime: values.endTime,
            }
          : undefined
      }
    />
  )

  return (
    <div
      className="flex flex-col flex-1 h-full min-h-0 overflow-hidden"
      data-testid="public-reservation-modal-details-map-container"
    >
      <PlanViewer
        venuePlans={venuePlans}
        tables={mapTables}
        selectedTableId={values.tableId}
        disabledTables={disabledTables}
        onTableSelect={onTableSelect}
        showSchedule={showSchedule}
        showTableLabels
        showZoomInTabs
        scheduleComponent={
          scheduleOnboardingTooltip ? (
            <OnboardingTooltip
              open={scheduleOnboardingTooltip.open}
              title={scheduleOnboardingTooltip.title}
              placement={scheduleOnboardingTooltip.placement}
              positionClassName={scheduleOnboardingTooltip.positionClassName}
              tooltipClassName={scheduleOnboardingTooltip.tooltipClassName}
              data-testid={scheduleOnboardingTooltip["data-testid"]}
            >
              {scheduleElement}
            </OnboardingTooltip>
          ) : (
            scheduleElement
          )
        }
      />
    </div>
  )
}

export default ReservationDetailsWithPlan
